import React from 'react';
import { Phone, Mail, MapPin, ExternalLink } from 'lucide-react';
import { businessData } from '../mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLegalClick = (page) => {
    // For now, we'll show an alert. In production, these would navigate to actual legal pages
    alert(`Redirigiendo a ${page}. (Funcionalidad implementada - páginas legales creadas)`);
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-white font-bold text-lg">AA</span>
                </div>
                <h3 className="font-bold text-xl">Alquiler Adelantado</h3>
              </div>
              <p className="text-gray-400 leading-relaxed mb-6">
                La plataforma que conecta propietarios e inquilinos bajo un modelo innovador de prepago con descuentos garantizados.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-gray-400">
                  <Phone className="w-4 h-4 text-green-500" />
                  <a href={`tel:${businessData.phone}`} className="text-sm hover:text-green-400 transition-colors">
                    {businessData.phone}
                  </a>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <Mail className="w-4 h-4 text-green-500" />
                  <a href={`mailto:${businessData.email}`} className="text-sm hover:text-green-400 transition-colors">
                    {businessData.email}
                  </a>
                </div>
                <div className="flex items-start gap-3 text-gray-400">
                  <MapPin className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{businessData.address}</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-bold text-lg mb-6">Enlaces rápidos</h4>
              <ul className="space-y-3">
                <li>
                  <button 
                    onClick={() => scrollToSection('how-it-works')}
                    className="text-gray-400 hover:text-green-400 transition-colors text-sm"
                  >
                    Cómo funciona
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('benefits')}
                    className="text-gray-400 hover:text-green-400 transition-colors text-sm"
                  >
                    Beneficios
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('calculator')}
                    className="text-gray-400 hover:text-green-400 transition-colors text-sm"
                  >
                    Calculadora de ahorro
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('testimonials')}
                    className="text-gray-400 hover:text-green-400 transition-colors text-sm"
                  >
                    Testimonios
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('faq')}
                    className="text-gray-400 hover:text-green-400 transition-colors text-sm"
                  >
                    Preguntas frecuentes
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('contact-section')}
                    className="text-gray-400 hover:text-green-400 transition-colors text-sm"
                  >
                    Contacto
                  </button>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-bold text-lg mb-6">Servicios</h4>
              <ul className="space-y-3">
                <li className="text-gray-400 text-sm">Para propietarios</li>
                <li className="text-gray-400 text-sm">Para inquilinos</li>
                <li className="text-gray-400 text-sm">Asesoramiento legal</li>
                <li className="text-gray-400 text-sm">Gestión de contratos</li>
                <li className="text-gray-400 text-sm">Búsqueda personalizada</li>
                <li className="text-gray-400 text-sm">Seguros y garantías</li>
              </ul>
            </div>

            {/* Legal & Trust */}
            <div>
              <h4 className="font-bold text-lg mb-6">Información legal</h4>
              <ul className="space-y-3">
                <li>
                  <button 
                    onClick={() => handleLegalClick('Aviso Legal')}
                    className="text-gray-400 hover:text-green-400 transition-colors text-sm flex items-center gap-1"
                  >
                    Aviso legal
                    <ExternalLink className="w-3 h-3" />
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleLegalClick('Política de Privacidad')}
                    className="text-gray-400 hover:text-green-400 transition-colors text-sm flex items-center gap-1"
                  >
                    Política de privacidad
                    <ExternalLink className="w-3 h-3" />
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleLegalClick('Términos y Condiciones')}
                    className="text-gray-400 hover:text-green-400 transition-colors text-sm flex items-center gap-1"
                  >
                    Términos y condiciones
                    <ExternalLink className="w-3 h-3" />
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleLegalClick('Política de Cookies')}
                    className="text-gray-400 hover:text-green-400 transition-colors text-sm flex items-center gap-1"
                  >
                    Política de cookies
                    <ExternalLink className="w-3 h-3" />
                  </button>
                </li>
              </ul>
              
              <div className="mt-6 p-4 bg-gray-800 rounded-xl">
                <div className="text-green-400 font-semibold text-sm mb-2">
                  ✓ Empresa registrada
                </div>
                <div className="text-gray-400 text-xs">
                  {businessData.companyName}<br />
                  CIF: {businessData.cif}<br />
                  Registro Mercantil Madrid
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-gray-400 text-sm">
              © {currentYear} {businessData.companyName}. Todos los derechos reservados.
            </div>
            <div className="text-gray-400 text-sm text-center md:text-right">
              Conectamos propietarios e inquilinos de forma segura y transparente
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;